import useScreenDimensions from "../hooks/useScreenDimensions";
import { SidebarItem } from "./sidebarItem";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { CampaignFrontend } from "../syncedTypes";
import useCampaigns from "../hooks/useCampaigns";
import { useAuth, useClerk, UserButton } from "@clerk/clerk-react";
import useUser from "../hooks/useUser";
import next from "../resources/next.svg";
import "./sidebar.css";
import { StandardButton } from "./standardButton";
import { CampaignModalContext } from "../Pages/dashboard";
import creditCardLogo from "../resources/creditCard.svg";
import accountLogo from "../resources/accountIcon.svg";
import { datasource } from "../datasource/datasource";
import useOrganziations from "../hooks/useOrganizations";
import { useCannotCreateNewCampaignModal } from "../hooks/useCannotCreatNewCampaignModal";
import { StandardInput } from "./standardInput";
import { createOnChangeHandler } from "../lib/helpers";
import { useDontHaveASubModal } from "./DontHaveASubModal";
import { isProd } from "../isProd";
import { useShepherdGlobalState } from "../hooks/useShepherdGlobalState";

export interface SideBarItemData extends CampaignFrontend {
  isSelected: boolean;
}

const CreditCardIcon = () => {
  return <img src={creditCardLogo} />;
};

const AccountIcon = () => {
  return <img src={accountLogo} />;
};

const PaymentAndSubscriptionPage = () => {
  const [updatePaymentUrl, setUpdatePaymentUrl] = useState<string>("");
  const [updateSubUrl, setUpdateSubUrl] = useState<string>("");
  const [cancelSubUrl, setCancelSubUrl] = useState<string>("");
  const [generalPortalUrl, setGeneralPortalUrl] = useState<string>("");
  const user = useUser((state) => state.user);

  const org = useOrganziations((state) => state.activeOrganization);
  const { getToken } = useAuth();
  useEffect(() => {
    const runThis = async () => {
      if (org?.planTypeId === 4) {
        return;
      }
      const token = await getToken();
      const session = await datasource.getStripeUpdatePaymentSession(
        org?.id as number,
        token as string
      );
      setUpdatePaymentUrl(session.updatePayment);
      setUpdateSubUrl(session.updateSub);
      setCancelSubUrl(session.cancelSub);
      setGeneralPortalUrl(session.generalPortal);

    };
    runThis();
  }, []);

  if (user?.accountType !== 1) {
    return <h3>You need to be an account administrator to access this option</h3>
  }

  return (
    <div>
      <h1 style={{fontSize: '2rem', paddingBottom: '.5rem'}}>Manage Payment and Subscriptions</h1>
      <h3 style={{paddingBottom: '2rem'}}>To make any changes to your plan, view your invoice history, or change your payment method, press the button below</h3>
      <StandardButton
        buttonText="Go To My Payment Portal"
        onClick={() => window.open(generalPortalUrl)}
      />
    </div>
  );
};

const TestOptions = () => {
  const setShouldOpenTour = useShepherdGlobalState(
    (state) => state.setShouldOpenTour
  );

  const onPress = () => {
    setShouldOpenTour(true);
  };

  return <StandardButton buttonText="Trigger Tutorial" onClick={onPress} />;
};

const UsersOnOrgPage = () => {
  const user = useUser((state) => state.user);
  const [users, setUsers] = useState<
    { firstName: string; lastName: string; email: string }[]
  >([]);
  const [hasLoaded, setHasLoaded] = useState<boolean>(false);
  const activeOrg = useOrganziations((state) => state.activeOrganization);
  const { getToken } = useAuth();
  useEffect(() => {
    getToken().then((token) =>
      datasource
        .getUsersOnOrg(activeOrg?.id as number, token as string)
        .then((users) => {
          setUsers(users.map((el: any) => el.user));
          setHasLoaded(true);
        })
    );
  }, [activeOrg, getToken]);

  const [email, setEmail] = useState<string>("");
  const onEmailUpdate = createOnChangeHandler(setEmail);

  const [link, setLink] = useState<string | null>(null);

  const onLinkCreateButtonPress = useCallback(async () => {
    const token = await getToken();
    const link = await datasource.createSignUpLink(
      activeOrg?.id as number,
      email,
      token as string
    );
    setLink(window.location.host + "/signup/" + link);
  }, [email]);

  // const { openSnackBar } = useContext(DashboardContext);

  const onCopyPress = useCallback(() => {
    window.navigator.clipboard.writeText(link as string);
    // openSnackBar();
  }, [link]);

  if (activeOrg?.planTypeId === 1 || activeOrg?.planTypeId === 2) {
    return (
      <div>
        You must be subscribed to the Unlimited Plan in order to add additional
        users
      </div>
    );
  }

  if (user?.accountType !== 1) {
    return <h3>You need to be an account administrator to access this option</h3>
  }
  return (
    <div>
      <div
        style={{ fontSize: "1.25rem", fontWeight: 500, marginBottom: "1rem" }}
      >
        {hasLoaded
          ? "Users who have access to this organization"
          : "Loading Users"}
      </div>
      <div style={{ marginBottom: "1rem" }}>
        {users.map((el) => (
          <div>
            <span>{`${el.firstName} `}</span>
            <span>{`${el.lastName}: `}</span>
            <span>{el.email}</span>
          </div>
        ))}
      </div>
      <StandardInput
        label={"Generate Link To Add A New User:"}
        placeHolder="TheirAddress@email.com"
        value={email}
        onChange={onEmailUpdate}
      />
      <StandardButton
        onClick={onLinkCreateButtonPress}
        buttonText="Generate Link"
        customStyles={{ width: "5rem", marginTop: "1rem" }}
      />
      <div>
        {link ? (
          <div>
            <div>Send them this link to create their account</div>
            <StandardInput value={link} onChange={() => undefined} label="" />
            <StandardButton buttonText="Copy Link" onClick={onCopyPress} />
          </div>
        ) : null}
      </div>
    </div>
  );
};

export const SideBar = () => {
  const campaigns = useCampaigns((state) => state.allCampaigns);
  const setAllReviews = useCampaigns((state) => state.setCampaignToViewAll);

  const org = useOrganziations((state) => state.activeOrganization);

  const { setOpenNewCampaign } = useContext(CampaignModalContext);

  const setOpenCannotCreateCampaignModal = useCannotCreateNewCampaignModal(
    (state) => state.openAsNewCampaign
  );

  const setDontHaveSubModal = useDontHaveASubModal((state) => state.setIsOpen);

  const allCampaigns = useCampaigns((state) => state.allCampaigns);

  const onAddNewCampaignButtonPress = useCallback(() => {
    if (
      org?.planTypeId === 3 ||
      org?.planTypeId === 4 ||
      (org?.planTypeId === 2 && allCampaigns.length < 3)
    ) {
      setOpenNewCampaign();
    } else if (org?.planTypeId === 1) {
      setDontHaveSubModal(true);
    } else {
      setOpenCannotCreateCampaignModal();
    }
  }, [org, setOpenNewCampaign, setOpenCannotCreateCampaignModal, allCampaigns]);

  const user = useUser((state) => state.user);

  const { height } = useScreenDimensions();

  const heightInRem = height / 16;

  return (
    <div style={{ minWidth: "20rem" }}>
      <div
        style={{
          backgroundColor: "white",
          borderRadius: 16,
          height: "5rem",
          marginTop: "1rem",
          marginLeft: "1rem",
          display: "flex",
          alignItems: "center",
        }}
      >
        <div className="ml-4 flex justify-between" style={{ width: "100%" }}>
          <UserButton>
            <UserButton.UserProfilePage
              label="Payment And Plan"
              url="payment"
              labelIcon={<CreditCardIcon />}
            >
              <PaymentAndSubscriptionPage />
            </UserButton.UserProfilePage>
            
            <UserButton.UserProfilePage
              label="Users"
              url="users"
              labelIcon={<AccountIcon />}
            >
              <UsersOnOrgPage />
            </UserButton.UserProfilePage>
            {isProd ? null : (
              <UserButton.UserProfilePage
                label="TEST OPTIONS"
                url="Test"
                labelIcon={<AccountIcon />}
              >
                <TestOptions />
              </UserButton.UserProfilePage>
            )}
          </UserButton>
          <div className="flex flex-col ml-6 mr-8">
            <div style={{ fontWeight: 500, fontSize: 18 }}>
              {user?.firstName + " " + user?.lastName}
            </div>
            <div style={{ fontSize: 14, color: "#899091" }}>{user?.email}</div>
          </div>
        </div>
      </div>
      <div
        style={{
          backgroundColor: "white",
          borderRadius: 16,
          marginTop: "1rem",
          marginLeft: "1rem",
          display: "flex",
          alignItems: "start",
          justifyContent: "start",
          minHeight: `${heightInRem - 9}rem`,
          position: "relative",
          paddingBottom: "4rem",
          marginBottom: 8,
        }}
      >
        <div style={{ borderRadius: 24, width: "100%" }}>
          <div
            style={{
              borderRadius: 24,
              borderWidth: 1,
              borderColor: "#F1F1F1",
              display: "flex",
              justifyContent: "space-between",
              paddingLeft: "1rem",
              paddingRight: "1rem",
              height: "3rem",
              alignItems: "center",
              marginTop: "1rem",
              marginLeft: "1rem",
              marginRight: "1rem",
              marginBottom: "2rem",
            }}
            className="allReviewsButton"
            onClick={setAllReviews}
          >
            <span>All Reviews</span>
            <img src={next} style={{ height: ".7rem" }} />
          </div>
          {campaigns.map((el, i) => {
            return <SidebarItem campaign={el} index={i} key={el.hash} />;
          })}
          <div style={{ padding: "1rem" }}>
            <StandardButton
              buttonColor={"#1F1F1F"}
              buttonText="+  Add New Campaign"
              onClick={onAddNewCampaignButtonPress}
              customStyles={{ fontSize: "1rem" }}
            />
            <div
              style={{
                position: "absolute",
                bottom: "1rem",
                display: "flex",
                justifyContent: "center",
                width: "100%",
                left: 0,
              }}
            >
              <img src={"/logo.svg"} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
